import React from 'react';

/**
 * Shared ReactMarkdown components for table/list rendering.
 * Used by TriageDetailsModal and AISummaryAccordion to avoid duplication.
 */
export const markdownComponents = {
  ul: ({ node, ...props }: any) => <ul className="list-disc" {...props} />,
  ol: ({ node, ...props }: any) => <ol className="list-decimal" {...props} />,
  table: ({ node, children, ...props }: any) => (
    <table className="w-full border-collapse border border-default-300 table-fixed" style={{ tableLayout: 'fixed', width: '100%' }} {...props}>
      {children}
    </table>
  ),
  thead: ({ node, ...props }: any) => <thead {...props} />,
  tbody: ({ node, ...props }: any) => <tbody {...props} />,
  tr: ({ node, ...props }: any) => <tr {...props} />,
  td: ({ node, children, ...props }: any) => (
    <td className="border border-default-300 px-3 py-2 text-left break-words" style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }} {...props} title={typeof children === 'string' ? children : String(children)}>
      {children}
    </td>
  ),
  th: ({ node, ...props }: any) => (
    <th className="border border-default-300 px-3 py-2 text-left font-semibold bg-default-100" {...props} />
  ),
};

/** Shared inline style for markdown content (color, background, width). */
export const MARKDOWN_CONTENT_STYLE: React.CSSProperties = {
  color: 'black',
  backgroundColor: 'transparent',
  display: 'inline-block',
  maxWidth: '100%',
};

const MARKDOWN_CANDIDATE_SELECTOR = 'p, span, li, td, th, div, h1, h2, h3, h4, h5, h6';
const SKIP_MARKDOWN_TAGS = new Set(['img', 'a', 'script', 'style', 'pre', 'code', 'table', 'svg', 'ul', 'ol']);
const BOLD_MARKDOWN_PATTERN = /\*\*[^*\n]+\*\*/;
const LIST_MARKDOWN_PATTERN = /(?:^|\n)\s*[-*+]\s+(?:\*\*)?\S/;
const LIST_LINE_PATTERN = /^\s*[-*+]\s+(.+)$/;
const DITA_ESCAPE_PATTERN = /\\([\\`*_{}[\]()#+\-.!])/g;

function containsMarkdownSyntax(text: string): boolean {
  return BOLD_MARKDOWN_PATTERN.test(text) || LIST_MARKDOWN_PATTERN.test(text);
}

function hasOnlyTextOrBrChildren(element: HTMLElement): boolean {
  return Array.from(element.childNodes).every((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      return true;
    }
    return child.nodeType === Node.ELEMENT_NODE && (child as HTMLElement).tagName === 'BR';
  });
}

function getElementDepth(element: Element): number {
  let depth = 0;
  let node = element.parentElement;
  while (node) {
    depth += 1;
    node = node.parentElement;
  }
  return depth;
}

function getMarkdownSource(element: HTMLElement): string {
  return Array.from(element.childNodes)
    .map((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent ?? '';
      }
      if (node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).tagName === 'BR') {
        return '\n';
      }
      return '';
    })
    .join('');
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function unescapeDitaMarkdown(text: string): string {
  return text.replace(DITA_ESCAPE_PATTERN, '$1');
}

function renderInlineMarkdown(text: string): string {
  const unescaped = unescapeDitaMarkdown(text);
  const segments: string[] = [];
  let lastIndex = 0;
  const pattern = /\*\*([^*]+)\*\*/g;
  let match = pattern.exec(unescaped);

  while (match) {
    segments.push(escapeHtml(unescaped.slice(lastIndex, match.index)));
    segments.push(`<strong>${escapeHtml(match[1])}</strong>`);
    lastIndex = pattern.lastIndex;
    match = pattern.exec(unescaped);
  }

  segments.push(escapeHtml(unescaped.slice(lastIndex)));
  return segments.join('');
}

function closeMarkdownList(parts: string[], inList: boolean): boolean {
  if (inList) {
    parts.push('</ul>');
    return false;
  }
  return inList;
}

function markdownTextToHtml(markdown: string): string {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const parts: string[] = [];
  let inList = false;

  for (const line of lines) {
    const listMatch = LIST_LINE_PATTERN.exec(line);
    if (listMatch) {
      if (!inList) {
        parts.push('<ul>');
        inList = true;
      }
      parts.push(`<li>${renderInlineMarkdown(listMatch[1])}</li>`);
      continue;
    }

    inList = closeMarkdownList(parts, inList);
    const trimmed = line.trim();
    if (trimmed) {
      parts.push(`<p>${renderInlineMarkdown(trimmed)}</p>`);
    }
  }

  closeMarkdownList(parts, inList);
  return parts.join('');
}

function isMarkdownCandidate(element: HTMLElement): boolean {
  if (element.hasAttribute('data-markdown-rendered')) {
    return false;
  }
  if (SKIP_MARKDOWN_TAGS.has(element.tagName.toLowerCase())) {
    return false;
  }
  if (element.hasAttribute('data-dita-source_id')) {
    return false;
  }
  if (element.querySelector('img, a[data-dita-source_id], table, ul, ol')) {
    return false;
  }
  return hasOnlyTextOrBrChildren(element);
}

function setRenderedMarkdownContent(element: HTMLElement, html: string): void {
  const template = document.createElement('template');
  template.innerHTML = html;
  element.textContent = '';
  const fragment = document.createDocumentFragment();
  while (template.content.firstChild) {
    fragment.appendChild(template.content.firstChild);
  }
  element.appendChild(fragment);
}

/**
 * Renders markdown syntax embedded inside HTML text nodes (e.g. DITA span.ph.cmd).
 * Leaves structure, images, and data-dita-source_id elements untouched.
 */
export function applyEmbeddedMarkdownInHtml(container: HTMLElement): void {
  const candidates = Array.from(container.querySelectorAll(MARKDOWN_CANDIDATE_SELECTOR))
    .filter((element): element is HTMLElement => element instanceof HTMLElement)
    .filter(isMarkdownCandidate)
    .sort((a, b) => getElementDepth(b) - getElementDepth(a));

  for (const element of candidates) {
    const markdownSource = getMarkdownSource(element);
    if (!containsMarkdownSyntax(markdownSource)) {
      continue;
    }

    setRenderedMarkdownContent(element, markdownTextToHtml(markdownSource.trim()));
    element.setAttribute('data-markdown-rendered', 'true');
  }
}

/** Render sanitized DITA HTML into a container and apply embedded markdown. */
export function renderDitaHtmlIntoContainer(container: HTMLElement, htmlContent: string): void {
  container.innerHTML = htmlContent;
  applyEmbeddedMarkdownInHtml(container);

  const allAnchors = container.querySelectorAll('a');
  Array.from(allAnchors).forEach((anchor) => {
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer');
  });
}
